
import Vue from 'vue'
import Component from "vue-class-component";
import {Prop, VModel, Watch} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {uiBus} from "@/plugins/uiBus";
import {IStandaloneDeviceTypeOption, IListStandalone} from "@/model/Standalone/standaloneInterfaces";
import {IOnOffHandlers} from "bonomo/dist";
import {IAuthUserData} from "@/store/modules/authModule";
import {EUserPermissions} from "@/model/EUserPermissions";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import StandaloneScreenshotPreview from "@/views/standalone/StandaloneScreenshotPreview.vue";

const standaloneModule = namespace("standaloneModule");
const authModule = namespace("authModule");

@Component({
  components: {
    SimpleConfirmationDialog,
    StandaloneScreenshotPreview,
  }
})
export default class StandaloneEditDialog extends Vue {

  @standaloneModule.Action('updateStandaloneSim')
  private updateStandaloneSim!: ({standaloneId, sim}: {standaloneId: number; sim: string | null}) => Promise<any>;

  @standaloneModule.Action('updateStandaloneType')
  private updateStandaloneType!: ({standaloneId, digitalPortalType}: {standaloneId: number; digitalPortalType: string | null}) => Promise<any>;

  @standaloneModule.Action('updateStandaloneName')
  private updateStandaloneName!: ({standaloneId, name}: {standaloneId: number; name: string | null}) => Promise<any>;

  @standaloneModule.Action('deleteStandalone')
  private deleteStandalone!: ({standaloneId}: {standaloneId: number}) => Promise<any>;

  @standaloneModule.Action('fetchDeviceTypeOptions')
  private fetchDeviceTypeOptions!: () => void;

  @standaloneModule.Getter('deviceTypeOptions')
  private deviceTypeOptions!: IStandaloneDeviceTypeOption[];

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @VModel()
  protected dialogShowing!: boolean;

  @Prop({ required: true })
  private standalone!: IListStandalone;

  @Prop({ type: Number, required: true })
  private lockerId!: number;

  protected saving: boolean = false;
  protected submitting: boolean = false;
  protected deleting: boolean = false;
  protected nameInput: string = '';
  protected simInput: string = '';
  protected digitalPortalTypeInput: string | null = null;
  protected showRebootConfirmation: boolean = false;
  protected showRefreshConfirmation: boolean = false;
  protected showSshConfirmation: boolean = false;
  protected showScreenshot: boolean = false;
  protected showDeleteConfirmation: boolean = false;
  protected showSoftDeleteConfirmation: boolean = false;

  ons: IOnOffHandlers = {};

  public created() {
    this.ons = this.$ons({
      'backend.call.OK.rebootLocker': this.onRebootLockerOK,
      'backend.call.KO.rebootLocker': this.onRebootLockerKO,
      'backend.call.OK.sshConnection': this.onSshConnectionOK,
      'backend.call.KO.sshConnection': this.onSshConnectionKO,
      'backend.call.OK.refreshBrowser': this.onRefreshBrowserOK,
      'backend.call.KO.refreshBrowser': this.onRefreshBrowserKO,
    });
    this.fetchDeviceTypeOptions();
  }

  protected get typeItems(): IStandaloneDeviceTypeOption[] {
    return this.deviceTypeOptions;
  }

  protected get isOperator(): boolean {
    return PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_LOCKER_OPERATOR, this.standalone.community_id !== null ? this.standalone.community_id : 0);
  }

  protected get isDeveloper(): boolean {
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_DEVELOPER);
  }

  protected get canDelete(): boolean {
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_DEVELOPER)
      || PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_OPERATIONS);
  }

  @Watch('standalone', { immediate: true })
  onStandaloneChange(val: IListStandalone) {
    this.nameInput = (val && val.name) ? val.name : '';
    this.simInput = (val && val.sim) ? val.sim : '';
    this.digitalPortalTypeInput = (val && val.digital_portal_type) ? val.digital_portal_type : null;
  }

  protected async onSave() {
    this.saving = true;
    await Promise.all([
      this.updateStandaloneName({
        standaloneId: this.standalone.standalone_id,
        name: this.nameInput || null,
      }),
      this.updateStandaloneSim({
        standaloneId: this.standalone.standalone_id,
        sim: this.simInput || null,
      }),
      this.updateStandaloneType({
        standaloneId: this.standalone.standalone_id,
        digitalPortalType: this.digitalPortalTypeInput || null,
      }),
    ]);
    this.saving = false;
    uiBus.emit('success', this.$t('standalone.detail.saved'));
    this.dialogShowing = false;
  }

  protected onCancel() {
    this.dialogShowing = false;
  }

  protected getPreviewUrl() {
    const deviceId = String(this.standalone.device_id);
    const route = this.$router.resolve({
      name: 'standalonePreview',
      params: { deviceId, lockerId: String(this.standalone.locker_id) },
    });
    window.open(route.href, '_blank', 'noopener');
  }

  protected onRebootClicked() {
    this.showRebootConfirmation = true;
  }

  protected onRefreshClicked() {
    this.showRefreshConfirmation = true;
  }

  protected onSshConnectionClicked() {
    this.showSshConfirmation = true;
  }

  protected onScreenshotClicked() {
    this.showScreenshot = true;
  }

  protected onCloseScreenshot() {
    this.showScreenshot = false;
  }

  protected onRebootLocker() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/rebootStandalone', {
      standaloneId: this.standalone.standalone_id,
    });
  }

  private onRebootLockerOK() {
    this.submitting = false;
    this.showRebootConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onRebootLockerKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onRefreshBrowser() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/refreshBrowser', {
      standaloneId: this.standalone.standalone_id,
    });
  }

  private onRefreshBrowserOK() {
    this.submitting = false;
    this.showRefreshConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onRefreshBrowserKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onSshConnection() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/sshConnection', {
      standaloneId: this.standalone.standalone_id,
    });
  }

  private onSshConnectionOK() {
    this.submitting = false;
    this.showSshConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onSshConnectionKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected async onDeleteConfirmed() {
    this.deleting = true;
    await this.deleteStandalone({ standaloneId: this.standalone.standalone_id });
    this.deleting = false;
    this.showDeleteConfirmation = false;
    this.$uiBus.emit('success', this.$t('standalone.detail.delete.success'));
    this.dialogShowing = false;
    this.$emit('deleted', this.standalone.standalone_id);
  }

  protected async onSoftDeleteConfirmed() {
    this.deleting = true;
    await this.deleteStandalone({ standaloneId: this.standalone.standalone_id });
    this.deleting = false;
    this.showSoftDeleteConfirmation = false;
    this.$uiBus.emit('success', this.$t('standalone.detail.delete.success'));
    this.dialogShowing = false;
    this.$emit('deleted', this.standalone.standalone_id);
  }
}
